/**
 * @template T
 * @param {(this: any, ...args: T[]) => void} fn
 * @param {number} delay
 * @returns {(this: any, ...args: T[]) => void}
 */
function debounce(fn, delay) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * @param {KeyboardEvent} event
 */
function onKeyUpEscape(event) {
  if (event && event.key === 'Escape') {
    // Example: close filter panel or modal
    const openPanel = document.querySelector('.facets__disclosure[open]');
    if (openPanel) openPanel.removeAttribute('open');
  }
}
class FacetFiltersForm extends HTMLElement {
  constructor () {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    /** @param {Event} event */
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    /** @type {HTMLFormElement|null} */
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    /** @type {HTMLElement|null} */
    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    /**
     * @param {PopStateEvent} event
     */
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.toggle('disabled', disable);
      }
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    /** @type {HTMLElement|null} */
    const countContainer = document.getElementById('ProductCount');
    /** @type {HTMLElement|null} */
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    /** @type {HTMLElement|null} */
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      const collection = gridContainer.querySelector('.collection');
      if (collection) collection.classList.add('loading');
    }
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl) ?
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event) :
        FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        // Remove loading states
        FacetFiltersForm.clearLoadingStates();
      })
      .catch((error) => {
        console.warn('Filter fetch error:', error);
        FacetFiltersForm.clearLoadingStates();
        // Optionally show user-friendly error message
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer').innerHTML;
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    }
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      // Cast to HTMLElement for dataset access
      const el = /** @type {HTMLElement} */ (element);
      const index = el.dataset ? el.dataset.index : undefined;
      const target = index ? document.querySelector(`.js-filter[data-index="${index}"]`) : null;
      if (target) target.innerHTML = el.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));

    // CRITICAL FIX: Re-initialize price range sliders after DOM updates
    FacetFiltersForm.reinitializePriceRangeSliders();

    // Re‑wire accordion behavior so only one filter section is open at a time
    if (typeof window !== 'undefined' && typeof window.wireSingleFacetOpen === 'function') {
      window.wireSingleFacetOpen('#FacetsWrapperDesktop');
      window.wireSingleFacetOpen('#FacetFiltersFormMobile .mobile-facets__main');
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (activeFacetsElement && target) {
        target.innerHTML = activeFacetsElement.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) {
        target.innerHTML = source.innerHTML;
      }
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = mobileForm ? mobileForm.closest('menu-drawer') : null;
    // Only call bindEvents if menuDrawer is a custom element with that method
    if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    /** @type {HTMLElement|null} */
    const targetElement = target ? target.querySelector('.facets__selected') : null;
    /** @type {HTMLElement|null} */
    const sourceElement = source ? source.querySelector('.facets__selected') : null;
    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  // CRITICAL FIX: Re-initialize price range sliders after AJAX updates
  static reinitializePriceRangeSliders() {
    // Find all price-range elements and reinitialize them
    document.querySelectorAll('price-range').forEach((priceRange) => {
      if (priceRange.classList.contains('clean-price-filter')) {
        // Re-initialize clean price filter
        setTimeout(() => {
          if (priceRange.cleanPriceFilterInstance) {
            // Clean up existing instance
            priceRange.cleanPriceFilterInstance = null;
          }
          priceRange.cleanPriceFilterInstance = new CleanPriceFilter(priceRange);
        }, 100);
      } else if (priceRange && typeof priceRange.initRangeSlider === 'function') {
        // Small delay to ensure DOM is fully updated
        setTimeout(() => {
          priceRange.initRangeSlider();
        }, 50);
      }
    });

    // Also trigger custom event for clean filters
    document.dispatchEvent(new CustomEvent('facets:updated'));
  }

  // Clear all loading states to prevent fade-out issues
  static clearLoadingStates() {
    const gridContainer = document.getElementById('ProductGridContainer');
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    if (gridContainer) {
      const collection = gridContainer.querySelector('.collection');
      if (collection) collection.classList.remove('loading');
    }
    if (countContainer) countContainer.classList.remove('loading');
    if (countContainerDesktop) countContainerDesktop.classList.remove('loading');
  }

  onSubmitHandler(event) {
    if (event) {
      event.preventDefault();
    }
    const formElement = event?.target?.closest('form') || this.querySelector('form');
    if (!formElement) return;

    const formData = new FormData(formElement);
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event || { target: formElement });
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor () {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));

    this.setMinAndMaxValues();
  }

  /**
   * @param {Event} event
   */
  onRangeChange(event) {
    if (event && event.currentTarget) {
      const input = /** @type {HTMLInputElement} */ (event.currentTarget);
      this.adjustToValidValues(input);
    }
    this.setMinAndMaxValues();
    this.validateRange();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', String(maxInput.value));
    if (minInput.value) maxInput.setAttribute('min', String(minInput.value));
    if (minInput.value === '') maxInput.setAttribute('min', '0');
    if (maxInput.value === '') {
      const maxAttr = maxInput.getAttribute('max');
      minInput.setAttribute('max', maxAttr ? maxAttr : '');
    }
    this.validateRange();
  }

  validateRange() {
    const inputs = this.querySelectorAll('input');
    if (inputs.length < 2) return;
    const minValRaw = inputs[0].value;
    const maxValRaw = inputs[1].value;
    const container = this.querySelector('.facets__price');
    if (!container) return;

    const hasBoth = minValRaw !== '' && maxValRaw !== '';
    const minVal = Number(minValRaw);
    const maxVal = Number(maxValRaw);

    if (hasBoth && minVal > maxVal) {
      container.classList.add('facets__price--invalid');
    } else {
      container.classList.remove('facets__price--invalid');
    }
  }

  /**
   * @param {HTMLInputElement} input
   */
  /**
   * @param {HTMLInputElement} input
   */
  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));
    if (value < min) input.value = String(min);
    if (value > max) input.value = String(max);
  }

  // Initialize range slider
  initRangeSlider() {
    const container = this.classList.contains('facets__price')
      ? this
      : this.querySelector('.facets__price');
    if (!container) return;

    // Prevent duplicate slider creation when filters re-render
    if (container.querySelector('.facets__price-slider')) {
      return;
    }

    // Get dynamic min/max values
    const dataMin = container.getAttribute('data-min') || '0';
    const dataMax = container.getAttribute('data-max') || '1000';

    // Create slider HTML
    const sliderHTML = `
      <div class="facets__price-slider">
        <div class="price-range-slider">
          <input type="range" class="price-range-input" id="price-min-slider" min="${dataMin}" max="${dataMax}" value="${dataMin}" step="10">
          <input type="range" class="price-range-input" id="price-max-slider" min="${dataMin}" max="${dataMax}" value="${dataMax}" step="10">
        </div>
        <div class="price-range-labels">
          <span class="price-range-label" id="min-value">$${dataMin}</span>
          <span class="price-range-label" id="max-value">$${dataMax}</span>
        </div>
      </div>
    `;

    // Insert slider after price inputs
    const priceInputs = container.querySelector('.facets__price-inputs');
    if (priceInputs) {
      priceInputs.insertAdjacentHTML('afterend', sliderHTML);
    } else {
      // If no price inputs container, create one and add slider
      const inputs = container.querySelectorAll('input[type="number"]');
      if (inputs.length >= 2) {
        const inputsContainer = document.createElement('div');
        inputsContainer.className = 'facets__price-inputs';
        inputs[0].parentNode.insertBefore(inputsContainer, inputs[0]);
        inputsContainer.appendChild(inputs[0]);
        inputsContainer.appendChild(container.querySelector('.facets__price-range-separator'));
        inputsContainer.appendChild(inputs[1]);
        inputsContainer.insertAdjacentHTML('afterend', sliderHTML);
      }
    }

    this.setupSliderEvents();
  }

  // Setup slider event listeners
  setupSliderEvents() {
    const minSlider = /** @type {HTMLInputElement|null} */ (this.querySelector('#price-min-slider'));
    const maxSlider = /** @type {HTMLInputElement|null} */ (this.querySelector('#price-max-slider'));
    const numberInputs = this.querySelectorAll('input[type="number"]');
    const minInput = /** @type {HTMLInputElement|undefined} */ (numberInputs[0]);
    const maxInput = /** @type {HTMLInputElement|undefined} */ (numberInputs[1]);
    const minLabel = /** @type {HTMLElement|null} */ (this.querySelector('#min-value'));
    const maxLabel = /** @type {HTMLElement|null} */ (this.querySelector('#max-value'));
    const sliderTrack = /** @type {HTMLElement|null} */ (this.querySelector('.price-range-slider'));

    if (!minSlider || !maxSlider || !minInput || !maxInput) return;

    // Get min/max from data attributes or inputs
    const container = this.classList.contains('facets__price')
      ? this
      : this.querySelector('.facets__price');
    const globalMin = parseInt(container?.getAttribute('data-min') || minInput.getAttribute('min') || '0', 10);
    const globalMax = parseInt(container?.getAttribute('data-max') || maxInput.getAttribute('max') || '1000', 10);

    minSlider.min = String(globalMin);
    minSlider.max = String(globalMax);
    maxSlider.min = String(globalMin);
    maxSlider.max = String(globalMax);

    // Sync initial values
    minSlider.value = minInput.value || String(globalMin);
    maxSlider.value = maxInput.value || String(globalMax);

    // Update slider track visual
    const updateSliderTrack = () => {
      const minPercent = ((Number(minSlider.value) - globalMin) / (globalMax - globalMin)) * 100;
      const maxPercent = ((Number(maxSlider.value) - globalMin) / (globalMax - globalMin)) * 100;

      if (sliderTrack) {
        sliderTrack.style.setProperty('--min-percent', minPercent + '%');
        sliderTrack.style.setProperty('--max-percent', (100 - maxPercent) + '%');
      }
    };

    // Update labels
    const updateLabels = () => {
      if (minLabel && minSlider) minLabel.textContent = `$${minSlider.value}`;
      if (maxLabel && maxSlider) maxLabel.textContent = `$${maxSlider.value}`;
    };

    // Build preset chips
    const presetsContainer = container ? container.querySelector('.facets__price-presets') : null;
    const buildPresets = () => {
      if (!presetsContainer) return;

      presetsContainer.innerHTML = '';

      const range = globalMax - globalMin;
      if (range <= 0) return;

      const step = Math.max(Math.round(range / 3), 1);
      const middle1 = globalMin + step;
      const middle2 = globalMin + step * 2;

      const formatNumber = (value) => {
        const num = Number(value);
        if (Number.isNaN(num)) return String(value);
        try {
          return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
        } catch (e) {
          return String(num);
        }
      };

      const presets = [
        {
          min: globalMin,
          max: middle1,
          label: `Under $${formatNumber(middle1)}`,
        },
        {
          min: middle1,
          max: middle2,
          label: `$${formatNumber(middle1)} – $${formatNumber(middle2)}`,
        },
        {
          min: middle2,
          max: globalMax,
          label: `Over $${formatNumber(middle2)}`,
        },
      ];

      presets.forEach((preset) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'facets__price-preset';
        button.dataset.min = String(preset.min);
        button.dataset.max = String(preset.max);
        button.textContent = preset.label;
        presetsContainer.appendChild(button);
      });

      if (!presetsContainer.dataset.bound) {
        presetsContainer.addEventListener('click', (event) => {
          const target = event.target;
          if (!(target instanceof HTMLElement)) return;
          const button = target.closest('.facets__price-preset');
          if (!button) return;

          const presetMin = Number(button.dataset.min);
          const presetMax = Number(button.dataset.max);
          if (Number.isNaN(presetMin) || Number.isNaN(presetMax)) return;

          minSlider.value = String(presetMin);
          maxSlider.value = String(presetMax);
          minInput.value = String(presetMin);
          maxInput.value = String(presetMax);

          presetsContainer
            .querySelectorAll('.facets__price-preset--active')
            .forEach((el) => el.classList.remove('facets__price-preset--active'));
          button.classList.add('facets__price-preset--active');

          updateSliderTrack();
          updateLabels();
          this.onSliderRangeChange();

          // Trigger filter form submission so products are actually filtered
          const form = this.closest('form');
          if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          }
        });
        presetsContainer.dataset.bound = 'true';
      }
    };

    // Create and manage tooltips
    /** @type {(slider: HTMLElement, className: string) => HTMLElement|null} */
    const createTooltip = (slider, className) => {
      let tooltip = slider.parentElement?.querySelector(`.${className}`);
      if (!tooltip) {
        tooltip = document.createElement('div');
        tooltip.className = `price-slider-tooltip ${className}`;
        slider.parentElement?.appendChild(tooltip);
      }
      return tooltip;
    };

    const minTooltip = createTooltip(minSlider, 'price-slider-tooltip-min');
    const maxTooltip = createTooltip(maxSlider, 'price-slider-tooltip-max');

    // Show/hide and update tooltip
    /** @type {(slider: HTMLElement, tooltip: HTMLElement|null, value: string) => void} */
    const updateTooltip = (slider, tooltip, value) => {
      if (tooltip) {
        tooltip.textContent = `$${value}`;
        const percent = ((parseInt(value) - globalMin) / (globalMax - globalMin)) * 100;
        tooltip.style.left = `calc(${percent}% - ${tooltip.offsetWidth / 2}px)`;
      }
    };

    // Min slider events
    minSlider.addEventListener('input', (e) => {
      const target = /** @type {HTMLInputElement} */ (e.target);
      if (parseInt(target.value) >= parseInt(maxSlider.value)) {
        target.value = String(parseInt(maxSlider.value) - 10);
      }
      minInput.value = target.value;
      minSlider.setAttribute('aria-valuenow', target.value);
      updateSliderTrack();
      updateLabels();
      updateTooltip(minSlider, minTooltip, target.value);
      this.onRangeChange();
    });

    // Tooltip show/hide events for min slider
    minSlider.addEventListener('focus', () => {
      if (minTooltip) {
        minTooltip.style.display = 'block';
        minTooltip.style.opacity = '0.9';
        updateTooltip(minSlider, minTooltip, minSlider.value);
      }
    });

    minSlider.addEventListener('blur', () => {
      if (minTooltip) {
        minTooltip.style.opacity = '0';
        setTimeout(() => {
          if (minTooltip && minTooltip.style.opacity === '0') {
            minTooltip.style.display = 'none';
          }
        }, 150);
      }
    });

    maxSlider.addEventListener('input', (e) => {
      const target = /** @type {HTMLInputElement} */ (e.target);
      if (parseInt(target.value) <= parseInt(minSlider.value)) {
        target.value = String(parseInt(minSlider.value) + 10);
      }
      maxInput.value = target.value;
      maxSlider.setAttribute('aria-valuenow', target.value);
      updateSliderTrack();
      updateLabels();
      updateTooltip(maxSlider, maxTooltip, target.value);
      this.onSliderRangeChange();
    });

    // Tooltip show/hide events for max slider
    maxSlider.addEventListener('focus', () => {
      if (maxTooltip) {
        maxTooltip.style.display = 'block';
        maxTooltip.style.opacity = '0.9';
        updateTooltip(maxSlider, maxTooltip, maxSlider.value);
      }
    });

    maxSlider.addEventListener('blur', () => {
      if (maxTooltip) {
        maxTooltip.style.opacity = '0';
        setTimeout(() => {
          if (maxTooltip && maxTooltip.style.opacity === '0') {
            maxTooltip.style.display = 'none';
          }
        }, 150);
      }
    });

    // Keyboard accessibility for sliders
    [minSlider, maxSlider].forEach(slider => {
      slider.addEventListener('keydown', (e) => {
        let step = parseInt(slider.step) || 1;
        let value = parseInt(slider.value);
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          slider.value = String(Math.max(value - step, parseInt(slider.min)));
          slider.dispatchEvent(new Event('input'));
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          slider.value = String(Math.min(value + step, parseInt(slider.max)));
          slider.dispatchEvent(new Event('input'));
        }
      });
    });

    // Input events (sync back to sliders)
    minInput.addEventListener('input', (e) => {
      const target = /** @type {HTMLInputElement} */ (e.target);
      minSlider.value = target.value;
      updateSliderTrack();
      updateLabels();
    });

    maxInput.addEventListener('input', (e) => {
      const target = /** @type {HTMLInputElement} */ (e.target);
      maxSlider.value = target.value;
      updateSliderTrack();
      updateLabels();
    });

    // Initial setup
    updateSliderTrack();
    updateLabels();
    buildPresets();

    // Initialize tooltip positions
    updateTooltip(minSlider, minTooltip, minSlider.value);
    updateTooltip(maxSlider, maxTooltip, maxSlider.value);
  }

  // Handle range change with debouncing for slider events
  onSliderRangeChange() {
    clearTimeout(this.rangeTimeout);
    this.rangeTimeout = setTimeout(() => {
      const form = this.closest('facet-filters-form');
      if (form) {
        const formElement = /** @type {any} */ (form);
        if (typeof formElement.onSubmitHandler === 'function') {
          formElement.onSubmitHandler();
        }
      }
    }, 500);
  }

  // Override connectedCallback to initialize slider
  connectedCallback() {
    this.querySelectorAll('input').forEach(
      (element) => element.addEventListener('change', this.onRangeChange.bind(this))
    );

    // Check if this is a clean price filter
    if (this.classList.contains('clean-price-filter')) {
      // Initialize clean filter instead of regular slider
      setTimeout(() => {
        if (!this.cleanPriceFilterInstance) {
          this.cleanPriceFilterInstance = new CleanPriceFilter(this);
        }
      }, 100);
    } else {
      // Initialize regular slider after a short delay to ensure DOM is ready
      setTimeout(() => {
        this.initRangeSlider();
        this.syncSliderWithInputs();
      }, 100);
    }
  }

  // Sync the visual slider with the input values
  syncSliderWithInputs() {
    const minInput = this.querySelector('input[name*="gte"]');
    const maxInput = this.querySelector('input[name*="lte"]');
    const minSlider = this.querySelector('.price-range-input--min');
    const maxSlider = this.querySelector('.price-range-input--max');
    const sliderTrack = this.querySelector('.price-range-slider');

    if (!minSlider || !maxSlider || !sliderTrack) return;

    const minVal = minInput ? parseInt(minInput.value) || parseInt(minSlider.min) : parseInt(minSlider.min);
    const maxVal = maxInput ? parseInt(maxInput.value) || parseInt(maxSlider.max) : parseInt(maxSlider.max);

    minSlider.value = String(minVal);
    maxSlider.value = String(maxVal);

    // Update track visual
    const minPercent = ((minVal - parseInt(minSlider.min)) / (parseInt(minSlider.max) - parseInt(minSlider.min))) * 100;
    const maxPercent = ((maxVal - parseInt(maxSlider.min)) / (parseInt(maxSlider.max) - parseInt(maxSlider.min))) * 100;

    sliderTrack.style.setProperty('--min-percent', `${minPercent}%`);
    sliderTrack.style.setProperty('--max-percent', `${maxPercent}%`);

    // Update labels
    const minLabel = this.querySelector('.price-range-label--min');
    const maxLabel = this.querySelector('.price-range-label--max');
    if (minLabel) minLabel.textContent = `$${minVal}`;
    if (maxLabel) maxLabel.textContent = `$${maxVal}`;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor () {
    super();
    this.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
    });
  }
}

customElements.define('facet-remove', FacetRemove);

// Simplified global function for price range manipulation
/** @param {number|string} filterIndex @param {number} min @param {number} max */
function setPriceRange(filterIndex, min, max) {
  console.log(`Setting price range: ${min} - ${max}`);

  // Find price range element (simplified approach)
  const priceRangeElement = document.querySelector('price-range');
  if (!priceRangeElement) {
    console.error('No price-range element found');
    return;
  }

  // Find inputs
  const minInput = priceRangeElement.querySelector('input[name*="gte"]');
  const maxInput = priceRangeElement.querySelector('input[name*="lte"]');
  const minSlider = priceRangeElement.querySelector('.price-range-input--min');
  const maxSlider = priceRangeElement.querySelector('.price-range-input--max');

  if (!minInput || !maxInput) {
    console.error('Price inputs not found');
    return;
  }

  // Update input values
  minInput.value = String(min);
  maxInput.value = String(max);
  if (minSlider) minSlider.value = String(min);
  if (maxSlider) maxSlider.value = String(max);

  console.log(`Set values: min=${minInput.value}, max=${maxInput.value}`);

  // Update slider track visual
  const sliderTrack = priceRangeElement.querySelector('.price-range-slider');
  if (sliderTrack && minSlider && maxSlider) {
    const minPercent = ((min - parseInt(minSlider.min)) / (parseInt(minSlider.max) - parseInt(minSlider.min))) * 100;
    const maxPercent = ((max - parseInt(maxSlider.min)) / (parseInt(maxSlider.max) - parseInt(maxSlider.min))) * 100;
    sliderTrack.style.setProperty('--min-percent', `${minPercent}%`);
    sliderTrack.style.setProperty('--max-percent', `${maxPercent}%`);
  }

  // Submit form to apply filter
  const form = priceRangeElement.closest('form');
  if (form) {
    // Trigger change events to update the filter
    minInput.dispatchEvent(new Event('change', { bubbles: true }));
    maxInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

// Make function globally available
window.setPriceRange = setPriceRange;

// Initialize preset button event listeners when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  initializePricePresetButtons();
});

// Also initialize after AJAX updates
document.addEventListener('facets:updated', function () {
  initializePricePresetButtons();
});

function initializePricePresetButtons() {
  const presetButtons = document.querySelectorAll('.facets__price-preset[data-min][data-max]');

  presetButtons.forEach(button => {
    // Remove existing listeners to prevent duplicates
    button.removeEventListener('click', handlePresetClick);
    button.addEventListener('click', handlePresetClick);
  });
}

function handlePresetClick(event) {
  const button = event.target;
  const min = parseInt(button.dataset.min || '0');
  const max = parseInt(button.dataset.max || '0');
  const filterIndex = parseInt(button.dataset.filterIndex || '1');

  if (min >= 0 && max > 0) {
    setPriceRange(filterIndex, min, max);
  }
}

// Clean Price Filter Functionality
class CleanPriceFilter {
  constructor (element) {
    this.element = element;
    this.minInput = element.querySelector('input[name*="gte"], .clean-price-input[name*="gte"]');
    this.maxInput = element.querySelector('input[name*="lte"], .clean-price-input[name*="lte"]');
    this.minSlider = element.querySelector('.clean-range-min');
    this.maxSlider = element.querySelector('.clean-range-max');
    this.sliderRange = element.querySelector('.clean-slider-range');
    this.minLabel = element.querySelector('.clean-price-labels .clean-price-label:first-child');
    this.maxLabel = element.querySelector('.clean-price-labels .clean-price-label:last-child');
    this.presetButtons = element.querySelectorAll('.clean-preset-btn');
    this.statusText = element.querySelector('.clean-price-status');

    // Store reference to parent PriceRange element if this element is the container
    this.priceRangeElement = element.tagName.toLowerCase() === 'price-range' ? element : element.closest('price-range');

    this.init();
  }

  init() {
    if (!this.minInput || !this.maxInput || !this.minSlider || !this.maxSlider) return;

    // Get min/max values
    this.globalMin = parseInt(this.element.getAttribute('data-min') || '0');
    this.globalMax = parseInt(this.element.getAttribute('data-max') || '1000');

    // Setup event listeners
    this.setupInputListeners();
    this.setupSliderListeners();
    this.setupPresetListeners();

    // Initial update
    this.updateSliderRange();
    this.updateLabels();
    this.updateStatus();
  }

  setupInputListeners() {
    [this.minInput, this.maxInput].forEach(input => {
      if (!input) return;

      // Handle input events with debouncing for UI updates
      input.addEventListener('input', debounce(() => {
        this.syncFromInputs();
        this.updateSliderRange();
        this.updateLabels();
        this.updateStatus();
      }, 300));

      // Handle change events immediately for Shopify filtering
      input.addEventListener('change', () => {
        this.syncFromInputs();
        this.updateSliderRange();
        this.updateLabels();
        this.updateStatus();

        // Trigger PriceRange validation if available
        if (this.priceRangeElement && typeof this.priceRangeElement.setMinAndMaxValues === 'function') {
          this.priceRangeElement.setMinAndMaxValues();
        }
      });
    });
  }

  setupSliderListeners() {
    this.minSlider.addEventListener('input', () => {
      this.syncFromSliders();
      this.updateSliderRange();
      this.updateLabels();
      this.updateStatus();
    });

    this.maxSlider.addEventListener('input', () => {
      this.syncFromSliders();
      this.updateSliderRange();
      this.updateLabels();
      this.updateStatus();
    });
  }

  setupPresetListeners() {
    this.presetButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const min = parseInt(button.getAttribute('data-min') || '0');
        const max = parseInt(button.getAttribute('data-max') || this.globalMax);

        this.setValues(min, max);
        this.highlightActivePreset(button);
      });
    });
  }

  syncFromInputs() {
    let minVal = parseInt(this.minInput.value) || this.globalMin;
    let maxVal = parseInt(this.maxInput.value) || this.globalMax;

    // Ensure min doesn't exceed max
    if (minVal >= maxVal) {
      minVal = Math.max(this.globalMin, maxVal - 100);
      this.minInput.value = minVal;
    }

    this.minSlider.value = minVal;
    this.maxSlider.value = maxVal;
  }

  syncFromSliders() {
    if (!this.minSlider || !this.maxSlider || !this.minInput || !this.maxInput) return;

    let minVal = parseInt(this.minSlider.value) || this.globalMin;
    let maxVal = parseInt(this.maxSlider.value) || this.globalMax;

    // Ensure min doesn't exceed max
    if (minVal >= maxVal) {
      const buffer = Math.max(100, Math.floor((this.globalMax - this.globalMin) * 0.05));
      if (this.minSlider === document.activeElement) {
        maxVal = Math.min(this.globalMax, minVal + buffer);
        this.maxSlider.value = maxVal;
      } else {
        minVal = Math.max(this.globalMin, maxVal - buffer);
        this.minSlider.value = minVal;
      }
    }

    this.minInput.value = minVal;
    this.maxInput.value = maxVal;
  }

  setValues(min, max) {
    this.minInput.value = min;
    this.maxInput.value = max;
    if (this.minSlider) this.minSlider.value = min;
    if (this.maxSlider) this.maxSlider.value = max;

    this.updateSliderRange();
    this.updateLabels();
    this.updateStatus();

    // Trigger Shopify's PriceRange change events
    if (this.priceRangeElement && typeof this.priceRangeElement.setMinAndMaxValues === 'function') {
      this.priceRangeElement.setMinAndMaxValues();
    }

    // Trigger change events on inputs to activate Shopify filtering
    this.minInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.maxInput.dispatchEvent(new Event('change', { bubbles: true }));

    // Also trigger form submission as backup
    const form = this.element.closest('form');
    if (form) {
      // Use a small delay to ensure all events are processed
      setTimeout(() => {
        form.dispatchEvent(new Event('input', { bubbles: true }));
      }, 100);
    }
  }

  updateSliderRange() {
    if (!this.sliderRange || !this.minSlider || !this.maxSlider) return;

    const min = parseInt(this.minSlider.value) || this.globalMin;
    const max = parseInt(this.maxSlider.value) || this.globalMax;

    const range = this.globalMax - this.globalMin;
    if (range <= 0) return;

    const minPercent = Math.max(0, Math.min(100, ((min - this.globalMin) / range) * 100));
    const maxPercent = Math.max(0, Math.min(100, ((max - this.globalMin) / range) * 100));

    this.sliderRange.style.left = minPercent + '%';
    this.sliderRange.style.width = Math.max(0, maxPercent - minPercent) + '%';
  }

  updateLabels() {
    if (!this.minLabel || !this.maxLabel) return;

    const min = this.minSlider ? (parseInt(this.minSlider.value) || this.globalMin) : (parseInt(this.minInput.value) || this.globalMin);
    const max = this.maxSlider ? (parseInt(this.maxSlider.value) || this.globalMax) : (parseInt(this.maxInput.value) || this.globalMax);

    // Use the currency symbol from the page if available
    const currencySymbol = document.querySelector('.price .money')?.textContent?.match(/[^\d\s.,]/)?.[0] || '₹';

    this.minLabel.textContent = `${currencySymbol}${min.toLocaleString()}`;
    this.maxLabel.textContent = max >= this.globalMax ? `${currencySymbol}${max.toLocaleString()}+` : `${currencySymbol}${max.toLocaleString()}`;
  }

  updateStatus() {
    if (!this.statusText) return;

    const min = parseInt(this.minInput.value || this.globalMin);
    const max = parseInt(this.maxInput.value || this.globalMax);

    if (min === this.globalMin && max === this.globalMax) {
      this.statusText.textContent = 'All prices';
    } else {
      this.statusText.textContent = `₹${min.toLocaleString()} - ₹${max.toLocaleString()}`;
    }
  }

  highlightActivePreset(activeButton) {
    this.presetButtons.forEach(btn => btn.classList.remove('active'));
    activeButton.classList.add('active');

    setTimeout(() => {
      activeButton.classList.remove('active');
    }, 2000);
  }
}

// Initialize Clean Price Filters
function initializeCleanPriceFilters() {
  const cleanFilters = document.querySelectorAll('.clean-price-filter');
  cleanFilters.forEach(filter => {
    if (!filter.cleanPriceFilterInstance) {
      filter.cleanPriceFilterInstance = new CleanPriceFilter(filter);
    }
  });
}

// Initialize on DOM load
document.addEventListener('DOMContentLoaded', function () {
  // Small delay to ensure all other components are initialized first
  setTimeout(initializeCleanPriceFilters, 200);
});

// Re-initialize after facets updates (triggered by FacetFiltersForm.renderFilters)
document.addEventListener('facets:updated', function () {
  setTimeout(initializeCleanPriceFilters, 100);
});

// Also initialize after AJAX filter updates
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) { // Element node
            const cleanFilters = node.querySelectorAll ? node.querySelectorAll('.clean-price-filter') : [];
            if (cleanFilters.length > 0 || (node.classList && node.classList.contains('clean-price-filter'))) {
              setTimeout(initializeCleanPriceFilters, 50);
            }
          }
        });
      }
    });
  });

  // Start observing
  if (document.body) {
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    });
  }
}

// Also handle Shopify theme updates
if (typeof Shopify !== 'undefined' && Shopify.theme) {
  document.addEventListener('shopify:section:load', initializeCleanPriceFilters);
}

// Dynamic Price Filter - Enhanced for Auto-Population
class DynamicPriceFilter {
  constructor (container) {
    this.container = container;
    this.form = container.closest('form');
    const inputs = container.querySelectorAll('.dynamic-price-input');
    this.minInput = inputs[0];
    this.maxInput = inputs[1];
    this.presetButtons = container.querySelectorAll('.dynamic-preset-btn');

    this.bindEvents();
    this.syncWithExisting();
    this.initializeAutoValues();
  }

  bindEvents() {
    // Input change events
    if (this.minInput) {
      this.minInput.addEventListener('input', () => this.handleInputChange());
      this.minInput.addEventListener('blur', () => this.validateInputs());
    }

    if (this.maxInput) {
      this.maxInput.addEventListener('input', () => this.handleInputChange());
      this.maxInput.addEventListener('blur', () => this.validateInputs());
    }

    // Preset button events
    this.presetButtons.forEach(btn => {
      btn.addEventListener('click', () => this.handlePresetClick(btn));
    });

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }
  }

  initializeAutoValues() {
    // Auto-populate inputs if they have data attributes from Liquid
    if (this.minInput && this.minInput.dataset.autoMin) {
      this.minInput.placeholder = `Min: $${this.minInput.dataset.autoMin}`;
    }

    if (this.maxInput && this.maxInput.dataset.autoMax) {
      this.maxInput.placeholder = `Max: $${this.maxInput.dataset.autoMax}`;
    }

    // Initialize simple slider
    this.initializeSimpleSlider();
  }

  initializeSimpleSlider() {
    const slider = this.container.querySelector('.simple-price-slider');
    if (!slider) return;

    const track = slider.querySelector('.slider-track');
    const range = slider.querySelector('.slider-range');
    const minHandle = slider.querySelector('.slider-handle-min');
    const maxHandle = slider.querySelector('.slider-handle-max');

    if (!track || !range || !minHandle || !maxHandle) return;

    const minPrice = parseInt(slider.dataset.min) || 0;
    const maxPrice = parseInt(slider.dataset.max) || 999;
    const priceRange = maxPrice - minPrice;

    let isDragging = false;
    let activeHandle = null;

    const updateSlider = () => {
      const minVal = parseInt(this.minInput?.value || minPrice);
      const maxVal = parseInt(this.maxInput?.value || maxPrice);

      const minPercent = ((minVal - minPrice) / priceRange) * 100;
      const maxPercent = ((maxVal - minPrice) / priceRange) * 100;

      minHandle.style.left = `${Math.max(0, Math.min(100, minPercent))}%`;
      maxHandle.style.left = `${Math.max(0, Math.min(100, maxPercent))}%`;

      range.style.left = `${Math.max(0, Math.min(100, minPercent))}%`;
      range.style.right = `${Math.max(0, Math.min(100, 100 - maxPercent))}%`;
    };

    const handleStart = (e, handle) => {
      isDragging = true;
      activeHandle = handle;
      e.preventDefault();
    };

    const handleMove = (e) => {
      if (!isDragging || !activeHandle) return;

      const rect = track.getBoundingClientRect();
      const percent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      const value = Math.round(minPrice + (percent / 100) * priceRange);

      if (activeHandle === minHandle) {
        const maxVal = parseInt(this.maxInput?.value || maxPrice);
        const newMin = Math.min(value, maxVal - 1);
        if (this.minInput) this.minInput.value = newMin;
      } else if (activeHandle === maxHandle) {
        const minVal = parseInt(this.minInput?.value || minPrice);
        const newMax = Math.max(value, minVal + 1);
        if (this.maxInput) this.maxInput.value = newMax;
      }

      updateSlider();
      this.handleInputChange();
    };

    const handleEnd = () => {
      isDragging = false;
      activeHandle = null;
    };

    // Event listeners
    minHandle.addEventListener('mousedown', (e) => handleStart(e, minHandle));
    maxHandle.addEventListener('mousedown', (e) => handleStart(e, maxHandle));

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);

    // Touch events for mobile
    minHandle.addEventListener('touchstart', (e) => handleStart(e.touches[0], minHandle));
    maxHandle.addEventListener('touchstart', (e) => handleStart(e.touches[0], maxHandle));

    document.addEventListener('touchmove', (e) => handleMove(e.touches[0]));
    document.addEventListener('touchend', handleEnd);

    // Update slider when inputs change
    if (this.minInput) this.minInput.addEventListener('input', updateSlider);
    if (this.maxInput) this.maxInput.addEventListener('input', updateSlider);

    // Initial update
    updateSlider();
  } syncWithExisting() {
    // Get values from existing Shopify price range inputs
    const existingMinInput = document.querySelector('input[name="filter.v.price.gte"]');
    const existingMaxInput = document.querySelector('input[name="filter.v.price.lte"]');

    if (existingMinInput && this.minInput) {
      this.minInput.value = existingMinInput.value;
    }

    if (existingMaxInput && this.maxInput) {
      this.maxInput.value = existingMaxInput.value;
    }
  }

  handleInputChange() {
    this.clearActivePresets();
    this.updatePriceStatus();

    // Debounce the filtering
    clearTimeout(this.filterTimeout);
    this.filterTimeout = setTimeout(() => {
      this.applyFilter();
    }, 500);
  }

  handlePresetClick(btn) {
    console.log('Preset clicked:', btn.dataset.min, btn.dataset.max);
    const minPrice = btn.dataset.min;
    const maxPrice = btn.dataset.max;

    if (this.minInput) {
      this.minInput.value = minPrice;
      console.log('Set min input to:', minPrice);
    }
    if (this.maxInput) {
      this.maxInput.value = maxPrice;
      console.log('Set max input to:', maxPrice);
    }

    this.clearActivePresets();
    btn.classList.add('active');

    this.updatePriceStatus();
    this.applyFilter();
  }

  validateInputs() {
    const minVal = parseInt(this.minInput?.value || 0);
    const maxVal = parseInt(this.maxInput?.value || 999999);

    if (minVal > maxVal && this.maxInput?.value) {
      this.minInput.value = this.maxInput.value;
    }
  }

  clearActivePresets() {
    this.presetButtons.forEach(btn => btn.classList.remove('active'));
  }

  updatePriceStatus() {
    const statusElement = this.container.querySelector('.dynamic-price-current');
    const minVal = this.minInput?.value;
    const maxVal = this.maxInput?.value;

    if (statusElement) {
      if (minVal && maxVal) {
        statusElement.textContent = `$${minVal} - $${maxVal}`;
      } else if (minVal) {
        statusElement.textContent = `From $${minVal}`;
      } else if (maxVal) {
        statusElement.textContent = `Up to $${maxVal}`;
      } else {
        statusElement.textContent = 'All prices';
      }
    }
  }

  applyFilter() {
    // Update the hidden Shopify inputs
    const existingMinInput = document.querySelector('input[name="filter.v.price.gte"]');
    const existingMaxInput = document.querySelector('input[name="filter.v.price.lte"]');

    if (existingMinInput) {
      existingMinInput.value = this.minInput?.value || '';
    }

    if (existingMaxInput) {
      existingMaxInput.value = this.maxInput?.value || '';
    }

    // Submit the form to apply filters
    if (this.form) {
      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData);

      // Update URL and trigger collection reload
      history.replaceState(null, '', `${window.location.pathname}?${searchParams}`);

      // Trigger Shopify's filter system
      if (window.Shopify && window.Shopify.CollectionFilters) {
        window.Shopify.CollectionFilters.onFilterChange();
      } else {
        // Fallback: submit form normally
        this.form.submit();
      }
    }
  }

  handleFormSubmit(e) {
    // Let Shopify handle the form submission
    this.validateInputs();
  }
}

// Initialize Dynamic Price Filters
function initializeDynamicPriceFilters() {
  const dynamicFilters = document.querySelectorAll('.dynamic-price-filter');
  dynamicFilters.forEach(filter => {
    if (!filter.dynamicPriceFilterInstance) {
      filter.dynamicPriceFilterInstance = new DynamicPriceFilter(filter);
    }
  });
}

// Initialize on DOM load
document.addEventListener('DOMContentLoaded', function () {
  // Small delay to ensure all other components are initialized first
  setTimeout(initializeDynamicPriceFilters, 200);
});

// Re-initialize after facets updates
document.addEventListener('facets:updated', function () {
  setTimeout(initializeDynamicPriceFilters, 100);
});